import { useEffect, useRef, useState } from 'react';
import AskBox, { ChatMessages } from './components/AskBox';
import DemoDrawer from './components/DemoDrawer';
import ConciergeHome from './components/ConciergeHome';
import ConciergeWindow from './components/ConciergeWindow';
import LiveDemoPage from './components/LiveDemoPage';
import { getClientIdFromPath, getAnswerIdFromPath } from './lib/utils';
import { useCopy } from './lib/copy';
import { useClient } from './context/ClientContext';
import { referralFromSearch, websiteVisitFromSearch } from './lib/referral';

/**
 * Full-page chat surface (non-live-demo mode).
 *
 * Extracted into its own component so that AskBox hooks (including
 * the global `spot` event listener) only mount when this surface is
 * actually rendered.  Previously AskBox was instantiated in App for
 * ALL modes, creating a phantom listener that doubled every request
 * when the live-demo ConciergeWindow was also open.
 */
function ConciergeFullPage({ clientId, initialQuery, initialAnswerId }) {
  const { clientConfig } = useClient();
  const t = useCopy();
  const drawerConfig = clientConfig?.drawer;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 640 : false,
  );

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 640);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollContainerRef = useRef(null);
  const chat = AskBox({
    clientId,
    personaId: null,
    initialQuery,
    initialAnswerId,
    scrollContainerRef,
  });

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-white">
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto pb-48">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          {chat.messages.length === 0 && (
            <ConciergeHome
              clientId={clientId}
              chat={chat}
              mode={isMobile ? 'mobile' : 'full'}
              showDevPanel
            />
          )}

          {chat.messages.length > 0 && (
            <div className="pt-12 pb-6">
              {clientConfig?.logoUrl && (
                <div className="mb-8 flex justify-center animate-fadeIn">
                  <button
                    type="button"
                    onClick={chat.clearConversation}
                    aria-label={t('Return to home')}
                    title={t('Return to home')}
                    className="relative cursor-pointer rounded-2xl border border-slate-100 bg-white/60 p-6 shadow-sm backdrop-blur-sm transition-colors duration-200 hover:bg-white/80 hover:shadow-md"
                  >
                    <img
                      src={clientConfig.logoUrl}
                      alt={`${clientConfig.orgName} logo`}
                      className="pointer-events-none h-auto w-auto max-h-20 max-w-xs object-contain"
                    />
                  </button>
                </div>
              )}

              <ChatMessages
                messages={chat.messages}
                messagesEndRef={chat.messagesEndRef}
                isLoading={chat.isLoading}
                statusMessage={chat.statusMessage}
                currentStage={chat.currentStage}
                currentTool={chat.currentTool}
                currentToolQuery={chat.currentToolQuery}
                currentToolTargets={chat.currentToolTargets}
                sessionId={chat.sessionId}
                clientId={chat.clientId}
                inlineFollowupQuestion={chat.inlineFollowupQuestion}
                inlineFollowupMessageIndex={chat.inlineFollowupMessageIndex}
                isInlineFollowupSubmitting={chat.isInlineFollowupSubmitting}
                onInlineFollowupAnswer={chat.onInlineFollowupAnswer}
                onInlineFollowupSkip={chat.onInlineFollowupSkip}
                onRetrySystemError={chat.onRetrySystemError}
                authPrompt={chat.authPrompt}
                authMessageIndex={chat.authMessageIndex}
                authFormOpen={chat.authFormOpen}
                authStep={chat.authStep}
                authEmail={chat.authEmail}
                authCode={chat.authCode}
                authManualCodeOpen={chat.authManualCodeOpen}
                authScenarioOptions={chat.authScenarioOptions}
                preferredScenarioCode={chat.preferredScenarioCode}
                authError={chat.authError}
                authSessionTtlDays={chat.authSessionTtlDays}
                isAuthSubmitting={chat.isAuthSubmitting}
                hideInstantAnswerChip={chat.hideInstantAnswerChip}
                onNextActionClick={chat.onNextActionClick}
                onHandoverFormSubmit={chat.onHandoverFormSubmit}
                onAuthEmailChange={chat.onAuthEmailChange}
                onAuthCodeChange={chat.onAuthCodeChange}
                onAuthStart={chat.onAuthStart}
                onAuthVerify={chat.onAuthVerify}
                onAuthSelectScenario={chat.onAuthSelectScenario}
                onAuthToggleManualCode={chat.onAuthToggleManualCode}
                onAuthOpenInline={chat.onAuthOpenInline}
                onAuthResetToEmail={chat.onAuthResetToEmail}
              />
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-slate-200 bg-white/90 shadow-lg backdrop-blur-sm">
        <div className="mx-auto max-w-4xl px-4 py-4 sm:px-6">
          {chat.input}
          {chat.messages.length > 0 && chat.conversationAuthInline && (
            <div className="mt-3 flex justify-center">{chat.conversationAuthInline}</div>
          )}
          {drawerConfig?.enabled && !isDrawerOpen && (
            <div className="mt-3 flex justify-center sm:hidden">
              <button
                type="button"
                onClick={() => setIsDrawerOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-4 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-200"
              >
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 15l7-7 7 7"
                  />
                </svg>
                {drawerConfig.tab_label || 'Info'}
              </button>
            </div>
          )}

          {chat.messages.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:flex-nowrap">
              {chat.conversationAuthBar}
              <span className="text-slate-300">·</span>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={chat.clearConversation}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-800"
                >
                  {t('New conversation')}
                </button>
                {chat.userInfo &&
                  (!chat.authIdentityLabel ||
                    chat.userInfo.user_name?.toLowerCase() !==
                      chat.authIdentityLabel.toLowerCase()) && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span className="text-xs text-slate-400">{chat.userInfo.user_name}</span>
                    </>
                  )}
              </div>
            </div>
          )}
          {chat.newConversationConfirm}
        </div>
      </div>

      {drawerConfig?.enabled && (
        <DemoDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          onOpen={() => setIsDrawerOpen(true)}
          config={drawerConfig}
          scenarioOptions={chat.demoDrawerScenarioOptions}
          onSelectScenario={(scenarioCode) => {
            setIsDrawerOpen(false);
            chat.onStartDrawerScenario?.(scenarioCode);
          }}
        />
      )}
    </div>
  );
}

function App() {
  const t = useCopy();
  const [invalidReferral] = useState(() => {
    try {
      referralFromSearch(window.location.search);
      websiteVisitFromSearch(window.location.search);
      return false;
    } catch {
      return true;
    }
  });
  const clientId = getClientIdFromPath();
  const { clientConfig, configError, isLoading } = useClient();
  const [isWindowOpen, setIsWindowOpen] = useState(false);
  const liveDemoPageConfig = clientConfig?.liveDemoPage;
  const isLiveDemoMode = Boolean(liveDemoPageConfig?.enabled);

  const urlParams = new URLSearchParams(window.location.search);
  const initialQuery = urlParams.get('q') || '';
  const initialAnswerId = getAnswerIdFromPath();

  const [windowSeed, setWindowSeed] = useState({
    key: initialAnswerId
      ? `answer:${initialAnswerId}`
      : initialQuery
        ? `query:${initialQuery}`
        : 'default',
    initialQuery,
    initialAnswerId,
    preferredScenarioCode: null,
  });

  useEffect(() => {
    if (isLiveDemoMode && (initialQuery || initialAnswerId)) {
      setIsWindowOpen(true);
    }
  }, [initialAnswerId, initialQuery, isLiveDemoMode]);

  const openConciergeWindow = () => {
    setIsWindowOpen(true);
  };

  const handleLiveDemoQuestion = (question, scenarioCode = null) => {
    setWindowSeed({
      key: `${scenarioCode || 'public'}:${question || 'open'}:${Date.now()}`,
      initialQuery: question || '',
      initialAnswerId: null,
      preferredScenarioCode: scenarioCode,
    });
    setIsWindowOpen(true);
  };

  if (invalidReferral) {
    return (
      <div role="alert" className="p-6 text-center text-slate-700">
        {t('This link has an invalid reference code. Please ask the team for a new link.')}
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <div className="px-4 text-center">
          <h1 className="mb-4 text-6xl font-bold text-slate-900">404</h1>
          <p className="mb-2 text-xl text-slate-600">Client not found</p>
          <p className="text-sm text-slate-500">
            Please access the app with a valid client ID in the URL
          </p>
        </div>
      </div>
    );
  }

  if (isLoading && !clientConfig) {
    return <div className="min-h-screen bg-brand-cloud" aria-hidden="true" />;
  }

  // The config could not be loaded, so this page does not know whose it is.
  // It says so instead of rendering generic defaults under the organisation's
  // URL: a visitor reading the wrong name has no way to tell it is wrong, and
  // neither did we for two diagnoses in one evening.
  if (configError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <div className="px-4 text-center">
          <p className="mb-2 text-xl text-slate-600">This page could not load its configuration</p>
          <p className="mb-6 text-sm text-slate-500">
            Nothing is shown rather than the wrong thing. Please try again in a moment.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  if (isLiveDemoMode) {
    return (
      <>
        <LiveDemoPage
          clientId={clientId}
          config={liveDemoPageConfig}
          orgName={clientConfig?.orgName || clientId}
          logoUrl={clientConfig?.logoUrl || null}
          knowledgeTrust={clientConfig?.knowledgeTrust || null}
          onOpenChat={openConciergeWindow}
          onAskQuestion={handleLiveDemoQuestion}
        />
        <ConciergeWindow
          clientId={clientId}
          isOpen={isWindowOpen}
          onOpen={openConciergeWindow}
          onClose={() => setIsWindowOpen(false)}
          ctaLabel={liveDemoPageConfig?.cta_label || 'Chat with Concierge'}
          initialQuery={windowSeed.initialQuery}
          initialAnswerId={windowSeed.initialAnswerId}
          preferredScenarioCode={windowSeed.preferredScenarioCode}
          windowKey={windowSeed.key}
        />
      </>
    );
  }

  return (
    <ConciergeFullPage
      clientId={clientId}
      initialQuery={initialQuery}
      initialAnswerId={initialAnswerId}
    />
  );
}

export default App;
